import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import TrimbleAddressAutocomplete from '@/components/TrimbleAddressAutocomplete';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const inputCls = "text-foreground";
const selectCls = "w-full px-3 py-2 rounded-lg bg-transparent border text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50";

const Field = ({ label, required, error, children }) => (
  <div>
    <label className="text-xs text-muted-foreground block mb-1">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    {children}
    {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
  </div>
);

const YesNo = ({ label, value, onChange }) => (
  <div>
    <p className="text-xs text-muted-foreground mb-1.5">{label}</p>
    <div className="flex items-center gap-4">
      {['No', 'Yes'].map(opt => (
        <button key={opt} type="button" onClick={() => onChange(opt)} className="flex items-center gap-2 cursor-pointer">
          <div className={cn(
            "w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors",
            value === opt ? 'border-primary bg-primary' : 'border-border'
          )}>
            {value === opt && <div className="w-1.5 h-1.5 rounded-full bg-primary-foreground" />}
          </div>
          <span className="text-sm text-foreground">{opt}</span>
        </button>
      ))}
    </div>
  </div>
);

export default function StepPersonalInfo({ form, set, errors, driverType }) {
  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-space font-semibold text-foreground text-base">Applicant Information</h3>
        <p className="text-xs text-muted-foreground mt-0.5">Personal details as they appear on your CDL</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="First Name" required error={errors.first_name}>
          <Input value={form.first_name} onChange={e => set('first_name', e.target.value)} className={cn(inputCls, errors.first_name && 'border-red-500/50')} />
        </Field>
        <Field label="Last Name" required error={errors.last_name}>
          <Input value={form.last_name} onChange={e => set('last_name', e.target.value)} className={cn(inputCls, errors.last_name && 'border-red-500/50')} />
        </Field>
        {/* Owner Operator only -- their own business entity, not
            relevant to a Company Driver applicant. Matches the same
            gating already applied in the admin edit form
            (DriverFormModal.jsx) and the driver's own self-service
            profile (DriverProfile.jsx). */}
        {driverType === 'Owner Operator' && (
          <Field label="Company Name (LLC/Corp)">
            <Input value={form.company_name} onChange={e => set('company_name', e.target.value)} className={inputCls} placeholder="Optional" />
          </Field>
        )}
        <Field label="Date of Birth" required error={errors.dob}>
          <Input type="date" value={form.dob} onChange={e => set('dob', e.target.value)} className={cn(inputCls, errors.dob && 'border-red-500/50')} />
        </Field>
        <Field label="EIN or SSN" required error={errors.ein}>
          <Input value={form.ein} onChange={e => set('ein', e.target.value)} className={cn(inputCls, errors.ein && 'border-red-500/50')} placeholder="XX-XXXXXXX or XXX-XX-XXXX" />
        </Field>
        <Field label="Phone Number" required error={errors.phone}>
          <Input value={form.phone} onChange={e => set('phone', e.target.value)} className={cn(inputCls, errors.phone && 'border-red-500/50')} placeholder="+1 (555) 000-0000" />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Email Address" required error={errors.email}>
            <Input value={form.email} onChange={e => set('email', e.target.value)} className={cn(inputCls, errors.email && 'border-red-500/50')} placeholder="you@email.com" />
          </Field>
        </div>
      </div>

      <div className="pt-2 border-t border-border">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">Current Address</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <TrimbleAddressAutocomplete
              label="Street Address *"
              value={form.address_street}
              placeholder="Start typing your address…"
              onChange={e => set('address_street', e.target.value)}
              onSelect={({ street, city, state, zip }) => {
                set('address_street', street);
                set('address_city', city);
                set('address_state', state);
                set('address_zip', zip);
              }}
            />
            {errors.address_street && <p className="text-xs text-red-400 mt-1">{errors.address_street}</p>}
          </div>
          <Field label="City" required error={errors.address_city}>
            <Input value={form.address_city} onChange={e => set('address_city', e.target.value)} className={cn(inputCls, errors.address_city && 'border-red-500/50')} />
          </Field>
          <Field label="State" required error={errors.address_state}>
            <select value={form.address_state} onChange={e => set('address_state', e.target.value)}
              className={cn(selectCls, errors.address_state ? 'border-red-500/50' : 'border-input')}>
              <option value="" className="bg-background">Select state…</option>
              {US_STATES.map(s => <option key={s} value={s} className="bg-background">{s}</option>)}
            </select>
          </Field>
          <Field label="Zip Code" required error={errors.address_zip}>
            <Input value={form.address_zip} onChange={e => set('address_zip', e.target.value)} className={cn(inputCls, errors.address_zip && 'border-red-500/50')} maxLength={10} />
          </Field>
        </div>
      </div>

      <div className="pt-2 border-t border-border">
        <p className="text-xs text-muted-foreground mb-3">Previous address if you've lived at the above address less than 3 years (optional)</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <TrimbleAddressAutocomplete
              label="Previous Street Address"
              value={form.prev_address_street}
              placeholder="Start typing your previous address…"
              onChange={e => set('prev_address_street', e.target.value)}
              onSelect={({ street, city, state, zip }) => {
                set('prev_address_street', street);
                set('prev_address_city', city);
                set('prev_address_state', state);
                set('prev_address_zip', zip);
              }}
            />
          </div>
          <Field label="City">
            <Input value={form.prev_address_city} onChange={e => set('prev_address_city', e.target.value)} className={inputCls} />
          </Field>
          <Field label="State">
            <select value={form.prev_address_state} onChange={e => set('prev_address_state', e.target.value)}
              className={cn(selectCls, 'border-input')}>
              <option value="" className="bg-background">Select state…</option>
              {US_STATES.map(s => <option key={s} value={s} className="bg-background">{s}</option>)}
            </select>
          </Field>
          <Field label="Zip Code">
            <Input value={form.prev_address_zip} onChange={e => set('prev_address_zip', e.target.value)} className={inputCls} maxLength={10} />
          </Field>
        </div>
      </div>

      <div className="pt-2 border-t border-border grid grid-cols-1 sm:grid-cols-2 gap-4">
        <YesNo label="Do you have a TWIC Card?" value={form.twic_card} onChange={v => set('twic_card', v)} />
        <YesNo label="Do you have a passport?" value={form.passport} onChange={v => set('passport', v)} />
      </div>
    </div>
  );
}